<template>
  <div class="payment-methods__buttons">
    <button
      v-show="canUseApplePay"
      class="special-button special-button--apple-pay"
      type="button"
      @click.prevent="selectMethod('ApplePay')"
    >
      Apple Pay
    </button>

    <div
      v-show="canUseGooglePay"
      id="sq-google-pay"
      @click.prevent="selectMethod('GooglePay')"
    />

    <div id="payment-method-gift-card" />

    <div id="payment-method-charge-room" />
  </div>
</template>

<script>
  import errorHandlers from '@/services/error-handlers';
  import { mapGetters } from 'vuex';

  export default {
    name: 'PaymentMethodsSquare',

    props: {
      forceSaveCards: {
        type: Boolean,
        default: false
      },

      giftCardReloadPaymentMethod: {
        type: [Object, Boolean],
        default: null
      },

      paymentGateway: {
        type: Object,
        required: true
      },

      selectedPaymentMethod: {
        type: String,
        default: ''
      },

      total: {
        type: Number,
        required: true
      }
    },

    data() {
      return {
        googlePay: null,
        applePay: null,
        squarePayment: null,
        paymentRequest: null,
        canUseApplePay: false,
        canUseGooglePay: false
      };
    },

    computed: {
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('orders', ['order'])
    },

    unmounted() {
      this.onDestroyed();
    },

    mounted() {
      this.onMounted();
    },

    methods: {
      onDestroyed() {
        if (this.applePay) this.applePay.destroy();
        if (this.googlePay) this.googlePay.destroy();
      },

      async onMounted() {
        const isApplePayEnabled = this.validatePaymentMethodSupport('apple-pay');

        const isGooglePaySupported = this.validatePaymentMethodSupport('google-pay');

        this.initializeSquare();

        await Promise.all([
          isApplePayEnabled && this.initializeApplePay(),
          isGooglePaySupported && this.initializeGooglePay()
        ]);

        this.$emit('set-loading-status', false);
      },

      validatePaymentMethodSupport(methodType) {
        const enabledMethods = {
          'apple-pay': this.paymentGateway.providers.Square.paymentMethods.applePayEnabled && (!this.forceSaveCards || this.paymentGateway.methods.giftCard?.paymentMethods.applePayRecurringEnabled),
          'google-pay': this.paymentGateway.providers.Square.paymentMethods.googlePayEnabled && (!this.forceSaveCards || this.paymentGateway.methods.giftCard?.paymentMethods.googlePayRecurringEnabled)
        };

        return enabledMethods[methodType];
      },

      initializeSquare() {
        this.squarePayment = window.Square.payments(
          this.paymentGateway.providers.Square.applicationId,
          this.paymentGateway.paymentLocationId
        );

        this.paymentRequest = this.squarePayment.paymentRequest({
          countryCode: 'US',
          currencyCode: 'USD',
          total: {
            amount: this.total.toString(),
            label: `${this.merchant.name} - Order #${this.order.ticketNumber}`
          }
        });
      },

      async initializeApplePay() {
        try {
          this.applePay = await this.squarePayment.applePay(this.paymentRequest);

          this.canUseApplePay = true;
          this.$emit('set-enabled-payment-method', 'apple-pay');
        }
        catch (error) {
          errorHandlers.silent(error);

          this.$emit('unsupported-method', 'ApplePay');
        }
      },

      async initializeGooglePay() {
        try {
          this.googlePay = await this.squarePayment.googlePay(this.paymentRequest);

          this.canUseGooglePay = true;
          this.$emit('set-enabled-payment-method', 'google-pay');

          const googlePayButtonTarget = this.$el.querySelector('#sq-google-pay');

          if (googlePayButtonTarget) {
            this.googlePay.attach('#sq-google-pay', {
              buttonColor: 'default',
              buttonSizeMode: 'fill',
              buttonType: 'long'
            });
          }
        }

        catch (error) {
          errorHandlers.silent(error);

          this.$emit('unsupported-method', 'GooglePay');
        }
      },

      handleTokenSuccess(details, token, method) {
        const { email, givenName, familyName, phone } = details.billing;
        const { billing, last4, brand } = details.card;

        this.$emit('submitted', {
          contactInfo: {
            email,
            fullName: givenName + (familyName ? ` ${familyName}` : ''),
            phoneNumber: phone
          },
          paymentMethod: {
            type: 'square-alt-payment',
            body: {
              billingAddress: {
                postalCode: billing.postalCode
              },
              cardNumberLastFour: last4,
              cardType: brand,
              paymentType: method,
              walletToken: token
            }
          }
        });
      },

      selectMethod(method) {
        this.$emit('before-submit', {
          method,
          onValidated: this.submitMethod
        });
      },

      async submitMethod(method) {
        try {
          let result;

          switch (method) {
            case 'ApplePay':
              result = await this.applePay.tokenize();
              break;
            case 'GooglePay':
              result = await this.googlePay.tokenize();
              break;
            default:
              break;
          }

          const { details, errors, status, token } = result;

          if (status === 'OK') this.handleTokenSuccess(details, token, method);
          else if (errors) this.$emit('error', errors[0]);
        }

        catch (error) {
          errorHandlers.silent(error);
        }
      }
    }
  };
</script>

<style lang="sass">
  #sq-google-pay
    border-radius: 4px
    height: var(--input-height-large)
    overflow: hidden
    background-color: #000 !important
    transition: opacity 150ms

  .gpay-card-info-container.black:hover
    background-color: #000 !important
</style>
